import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faArrowRight,
  faTriangleExclamation,
  faClock,
  faBox,
  faFolderOpen,
  faCartShopping,
  faListCheck,
  faCheck,
  faCheckDouble,
} from "@fortawesome/free-solid-svg-icons";
import { useCrmInsights } from "../hooks/useCrmInsights";

const TYPE_ICON = {
  task_overdue: faTriangleExclamation,
  task_due_soon: faClock,
  task_checklist: faListCheck,
  idea_stale: faBox,
  idea_draft: faBox,
  project_status: faFolderOpen,
  project_late: faFolderOpen,
  abandoned_cart: faCartShopping,
  paytr_failed: faTriangleExclamation,
};

const SEVERITY_COLOR = {
  high: "#ef4444",
  medium: "#f59e0b",
  low: "#3b82f6",
};

export default function NotificationBell({ theme }) {
  const navigate = useNavigate();
  const {
    items,
    total,
    loading,
    error,
    marking,
    refresh,
    markAsRead,
    markAllAsRead,
  } = useCrmInsights({ pollMs: 90000 });
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    refresh();
  };

  const handleClose = () => setAnchorEl(null);

  const go = (href) => {
    handleClose();
    if (href) navigate(href);
  };

  const handleMarkRead = (e, id) => {
    e.stopPropagation();
    markAsRead(id);
  };

  const handleMarkAllRead = (e) => {
    e.stopPropagation();
    markAllAsRead();
  };

  return (
    <>
      <Tooltip title="Bildirimler">
        <IconButton onClick={handleOpen} sx={{ color: theme.colors.text.secondary, flexShrink: 0 }}>
          <Badge color="warning" badgeContent={total} invisible={!total} max={99}>
            <FontAwesomeIcon
              icon={faBell}
              style={{ fontSize: "1.05rem", color: theme.colors.text.secondary }}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 8,
            sx: {
              mt: 0.75,
              width: 380,
              maxWidth: "calc(100vw - 24px)",
              background: theme.colors.surface.primary,
              border: `1px solid ${theme.colors.border.primary}`,
              borderRadius: theme.borderRadius.lg,
              boxShadow: theme.shadows.glass,
            },
          },
        }}
        MenuListProps={{ dense: true, sx: { py: 0 } }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${theme.colors.border.primary}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: theme.colors.text.primary }}>
              Bildirimler
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.colors.text.muted }}>
              {total > 0 ? `${total} okunmamış` : "Her şey güncel"}
            </Typography>
          </Box>
          {total > 0 && (
            <Button
              size="small"
              disabled={marking}
              onClick={handleMarkAllRead}
              startIcon={
                <FontAwesomeIcon icon={faCheckDouble} style={{ fontSize: 11 }} />
              }
              sx={{
                textTransform: "none",
                fontSize: 11,
                fontWeight: 600,
                minWidth: 0,
                flexShrink: 0,
                color: theme.colors.text.secondary,
              }}
            >
              Tümünü okundu
            </Button>
          )}
        </Box>

        {(loading || marking) && items.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography sx={{ fontSize: 12, color: theme.colors.error }}>{error}</Typography>
            <Button size="small" onClick={refresh} sx={{ mt: 1, textTransform: "none" }}>
              Yenile
            </Button>
          </Box>
        )}

        {!loading && !error && items.length === 0 && !marking && (
          <Box sx={{ px: 2, py: 2.5, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13, color: theme.colors.text.muted }}>
              Okunmamış bildirim yok
            </Typography>
          </Box>
        )}

        {!loading &&
          !error &&
          items.map((item) => {
            const color = SEVERITY_COLOR[item.severity] || theme.colors.text.secondary;
            const icon = TYPE_ICON[item.type] || faBell;
            return (
              <MenuItem
                key={item.id}
                onClick={() => go(item.href)}
                sx={{
                  alignItems: "flex-start",
                  gap: 1,
                  py: 1.25,
                  whiteSpace: "normal",
                  borderBottom: `1px solid ${theme.colors.border.primary}`,
                  borderLeft: `3px solid ${color}`,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "8px",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: `${color}18`,
                    mt: 0.25,
                  }}
                >
                  <FontAwesomeIcon icon={icon} style={{ fontSize: 12, color }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: theme.colors.text.primary,
                      lineHeight: 1.25,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: theme.colors.text.muted, mt: 0.25 }}>
                    {item.message}
                  </Typography>
                </Box>
                <Tooltip title="Okundu işaretle">
                  <IconButton
                    size="small"
                    disabled={marking}
                    onClick={(e) => handleMarkRead(e, item.id)}
                    sx={{
                      mt: 0.25,
                      color: theme.colors.text.muted,
                      "&:hover": { color: theme.colors.primary },
                    }}
                  >
                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            );
          })}

        <Divider />
        <MenuItem
          onClick={() => go("/dashboard#crm-reminders")}
          sx={{ justifyContent: "center", py: 1.25 }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: theme.colors.primary,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            Tüm hatırlatmalar
            <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 10 }} />
          </Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
